/**
 * A block's ⚙ panel — the section panel's Content tab for ONE block, opened
 * from the block itself (its toolbar, or a click on it). The same shared
 * AnchoredPanel shell; what it changes shows on the page at once, Done stores
 * it as ONE undo step, Esc or a click outside drops it.
 */

import { Copy, Trash2 } from 'lucide-react'
import { AnchoredPanel, PanelHeader, PanelNote, VioletButton } from '@twasta/page-runtime/customize'
import type { Block } from '@/pages/designers/page/types'
import { blockLabel, blockSpec } from './pageOps'
import { BlockFields, type PageOption } from './fields'

export default function BlockPanel({
  anchor, block, sectionName, pages, dirty, onChange, onDone, onCancel, onDelete, onCopy, designerHref,
}: {
  anchor: HTMLElement | null
  block: Block
  sectionName: string
  pages: PageOption[]
  dirty: boolean
  onChange: (next: Block) => void
  onDone: () => void
  onCancel: () => void
  onDelete: () => void
  onCopy: () => void
  designerHref: string
}) {
  const label = blockLabel(block)
  const spec = blockSpec(block.type)
  return (
    <AnchoredPanel anchor={anchor} onClose={onCancel} label={`${label} block`} width={400}>
      <PanelHeader title={label} sub={`In the ${sectionName.toLowerCase()} section`} />
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {spec ? (
          <>
            <PanelNote>Change it here, or click its words on the page and type.</PanelNote>
            <BlockFields block={block} pages={pages} showTitle={false} onChange={onChange} />
          </>
        ) : (
          <PanelNote>
            This {label.toLowerCase()} is changed in the{' '}
            <a href={designerHref} target="_top" rel="noreferrer" style={{ color: '#6d28d9', fontWeight: 600 }}>page designer</a>.
            You can still move, copy or delete it here.
          </PanelNote>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap', paddingTop: 6, borderTop: '1px solid var(--color-border, #f1f3f6)' }}>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <button type="button" onClick={onDelete} data-tw-action="site-edit-delete-block"
              style={{ height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#b42318', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Trash2 size={14} /> Delete
            </button>
            <VioletButton quiet hook="site-edit-copy-block" onClick={onCopy}><Copy size={14} /> Copy</VioletButton>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>
              {dirty ? 'Esc or a click outside drops them' : 'No changes yet'}
            </span>
            <VioletButton hook="site-edit-panel-done" onClick={onDone}>Done</VioletButton>
          </span>
        </div>
      </div>
    </AnchoredPanel>
  )
}
